import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatDateOnly, formatDateInUserTZ } from '../../utils/timezone';
import { Money } from '../../ui';
import { resolveDiscount, toCents, toDollars, computeLineItems } from '../../utils/discountMath';
import './PublicInvoice.css';

// PublicInvoice — v2.8.0 redesign.
//
// External-facing page. The single highest-stakes external impression
// FCP makes through this app. Editorial layout: large Fraunces title,
// generous whitespace, tabular figures throughout, restrained brand
// accent on the total + Pay button. No emoji.
//
// Always renders in light mode regardless of viewer's theme — invoices
// get printed and forwarded; dark backgrounds would burn through ink
// and make screenshot/PDF flow look broken.

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Pay ALWAYS asks the server where to go (docs/CLIENT_PORTAL.md §3). It used
// to jump straight to a cached `payment_url` — a Checkout Session URL, which
// Stripe expires after 24 hours, or a hosted-invoice URL, which also expires —
// so a client coming back the next day was sent to a dead page. The server
// answers the CURRENT link: the Stripe invoice's page when the invoice was
// sent through Stripe, or a new Checkout for the balance.
function PayButton({ token, total, company }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/invoices/public/${token}/pay`, { method: 'POST' });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError(data.error || 'Unable to process payment');
    } catch (err) {
      setError('Unable to connect to payment service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pinv-pay">
      <button onClick={handlePay} disabled={loading} className="pinv-pay__btn">
        {loading ? 'Connecting…' : (
          <>
            <span>Pay </span>
            <Money amount={total} className="pinv-pay__amount" />
          </>
        )}
      </button>
      {error && (
        <div className="pinv-pay__error">
          <p>{error}</p>
          <p className="pinv-pay__error-hint">
            Contact us to pay
            {company?.email && <>: <a href={`mailto:${company.email}`}>{company.email}</a></>}
            {company?.phone && ` or call ${company.phone}`}
          </p>
        </div>
      )}
    </div>
  );
}

// ── What a client is told when the DOCUMENT itself will not load ────────────
//
// The loader threw one `new Error('Not found')` for every failure — a 404, an
// nginx 502 while PM2 restarts `fcp-tracker`, a 500 from a bad query, a
// dropped connection — and rendered "Invoice not found or link has expired."
// for all of them. For anything but the 404 that is a FALSE statement with a
// business consequence: a client holding a bill reads "expired" as "this is no
// longer live", and the money does not get paid today.
//
// Neither sentence can offer a phone number: the company's details arrive in
// the payload of the request that just failed.
const LINK_GONE = 'Invoice not found or link has expired.';
const CANNOT_LOAD = 'We could not load this invoice just now — this is a problem on our '
  + 'end, not with your link. Please try again in a moment.';

function PublicInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // 404 is the only answer that means the link itself is finished. Every
    // other failure — including a body that will not parse — is ours.
    fetch(`/api/invoices/public/${token}`)
      .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'gone' : 'down'); return r.json(); })
      .then(d => setData(d))
      .catch(e => setError(e && e.message === 'gone' ? LINK_GONE : CANNOT_LOAD))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="pinv-shell pinv-shell--center">
      <div className="pinv-loading">Loading invoice…</div>
    </div>
  );

  if (error) return (
    <div className="pinv-shell pinv-shell--center">
      <div className="pinv-notfound">
        <h2>Invoice not available</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  const { invoice, items, photos, company } = data;
  const isPaid = invoice.status === 'paid';
  // Part-paid reads as owing, like sent: the balance row below says how much.
  const isSent = invoice.status === 'sent' || invoice.status === 'partial';
  const isCancelled = invoice.status === 'cancelled';
  const returnedFromPay = new URLSearchParams(window.location.search).get('paid') === 'true';
  const justPaid = isPaid && returnedFromPay;

  return (
    <div className="pinv-shell">
      <article className="pinv-doc">

        {/* ── Top mark + invoice number ─────────────────────────────── */}
        <header className="pinv-head">
          <div className="pinv-head__brand">
            {company?.logo && (
              <img src={company.logo} alt="" className="pinv-head__logo" />
            )}
            <div className="pinv-head__company">
              <h1 className="pinv-head__company-name">{company?.name}</h1>
              {company?.address && <div className="pinv-head__company-meta">{company.address}</div>}
              {company?.phone && <div className="pinv-head__company-meta">{company.phone}</div>}
              {company?.email && <div className="pinv-head__company-meta">{company.email}</div>}
            </div>
          </div>

          <div className="pinv-head__doc">
            <div className="pinv-head__label">Invoice</div>
            <div className="pinv-head__number fcp-tabular">{invoice.invoice_number}</div>
            <div className="pinv-head__dates">
              <div>
                <span className="pinv-head__date-label">Issued</span>
                <span className="pinv-head__date-value fcp-tabular">
                  {/* `invoices.created_at` is a bare SQLite CURRENT_TIMESTAMP —
                      "2026-03-03 01:00:00", space separator, no zone marker
                      (server/database.js:314) — and the public route hands it
                      over untouched (server/routes/invoices.js:265). `new Date()`
                      read that as LOCAL, so an invoice raised at 9pm Eastern was
                      dated TOMORROW on the document the client keeps. Same
                      family as Clients.js / Communication.js / Dashboard.js.

                      Formatted through the same helper as the "Due" line below
                      rather than left on `toLocaleDateString`, for two reasons:
                      it returns '' rather than throwing on a null or unparseable
                      value (the old code rendered "Jan 1, 1970" for a null), and
                      it pins the document to the company's timezone. Nobody is
                      logged in here, so `getUserTimezone()` falls through to
                      America/New_York — which is the date the office issued the
                      invoice on, and the date it will quote back on the phone.
                      Browser-local would disagree with that for a client in
                      another zone. */}
                  {formatDateOnly(invoice.created_at)}
                </span>
              </div>
              {invoice.due_date && (
                <div>
                  <span className="pinv-head__date-label">Due</span>
                  <span className="pinv-head__date-value fcp-tabular">
                    {formatDateOnly(invoice.due_date)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Status banner ─────────────────────────────────────────── */}
        {isPaid && (
          <div className="pinv-status pinv-status--paid">
            <span className="pinv-status__icon"><CheckIcon /></span>
            <span className="pinv-status__label">Paid</span>
            {invoice.paid_at && (
              <span className="pinv-status__meta fcp-tabular">
                {/* Same wire format as `created_at` above, and the same fix.
                    `paid_at` is written by `CURRENT_TIMESTAMP` on every path
                    that marks an invoice paid (server/routes/invoices.js:548,
                    :787, :981), including the Stripe webhook — so a card
                    payment taken at 8:30pm Eastern was receipted as the next
                    day. Kept on `formatDateInUserTZ` rather than
                    `formatDateOnly` only to preserve the long month name this
                    banner has always used. */}
                · {formatDateInUserTZ(invoice.paid_at, { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
        )}
        {/* A cancelled invoice has no Pay button; without this it read as an
            unpaid bill with the button missing. */}
        {isCancelled && (
          <div className="pinv-status pinv-status--cancelled" data-status="cancelled">
            <span className="pinv-status__label">Cancelled</span>
            <span className="pinv-status__meta">· nothing is owed</span>
          </div>
        )}
        {isSent && (
          <div className="pinv-status pinv-status--due">
            {/* "Part paid", not a second "Balance due" above the totals row
                of that name (review of #737). */}
            <span className="pinv-status__label">{invoice.status === 'partial' ? 'Part paid' : 'Payment due'}</span>
            {invoice.due_date && (
              <span className="pinv-status__meta fcp-tabular">
                · {invoice.status === 'partial' ? 'the rest by' : 'by'} {formatDateOnly(invoice.due_date)}
              </span>
            )}
          </div>
        )}

        {/* ── Bill to ───────────────────────────────────────────────── */}
        <section className="pinv-billto">
          <div className="pinv-billto__label">Bill to</div>
          <div className="pinv-billto__name">{invoice.client_name}</div>
          {invoice.client_address && <div className="pinv-billto__line">{invoice.client_address}</div>}
          {invoice.client_email && <div className="pinv-billto__line">{invoice.client_email}</div>}
        </section>

        {/* ── Items table ───────────────────────────────────────────── */}
        <section className="pinv-items">
          <table className="pinv-items__table responsive-cards">
            <thead>
              <tr>
                <th className="pinv-items__th">Description</th>
                <th className="pinv-items__th pinv-items__th--num">Qty</th>
                <th className="pinv-items__th pinv-items__th--num">Unit price</th>
                <th className="pinv-items__th pinv-items__th--num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                // SETTLED TO CENTS, the same way the stored subtotal was.
                // `quantity * unit_price` unrounded, handed to <Money>, formats
                // through Intl — and Intl rounds the decimal STRING while the
                // subtotal was built with Math.round(x * 100). They disagree
                // wherever the product lands near a half-cent:
                //   1.5 x $15.53 = 23.295
                //     Intl of the raw double        -> "$23.29"  printed here
                //     Math.round(23.295*100) = 2330 -> "$23.30"  in the total
                // Measured on this branch: 255 of 18,558 line shapes. A client
                // adding up this column got a different number from the
                // Subtotal underneath it. toCents/toDollars is the one
                // conversion discountMath and the Stripe path already use.
                const gross = toDollars(toCents((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)));
                const disc = resolveDiscount(gross, item.discount_amount, item.discount_type);
                const net = Math.max(0, gross - disc);
                const liPhotos = Array.isArray(item.photos) ? item.photos : [];
                return (
                <tr key={idx}>
                  <td className="pinv-items__td pinv-items__desc" data-label="Description">
                    {item.description}
                    {item.notes && <div className="pinv-items__note">{item.notes}</div>}
                    {disc > 0 && (
                      <div className="pinv-items__linedisc">
                        Discount: −<Money amount={disc} />
                        {item.discount_type === 'percent' && parseFloat(item.discount_amount) > 0 ? ` (${parseFloat(item.discount_amount)}%)` : ''}
                      </div>
                    )}
                    {liPhotos.length > 0 && (
                      <div className="pinv-items__photos">
                        {liPhotos.map((p, i) => (
                          <img key={i} className="pinv-items__photo"
                            src={p.file_path ? (p.file_path.startsWith('/uploads') ? p.file_path : `/uploads/${p.file_path}`) : ''}
                            alt={p.caption || 'line item photo'} loading="lazy"
                            onError={e => { e.target.style.display = 'none'; }} />
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="pinv-items__td pinv-items__td--num fcp-tabular" data-label="Qty">{item.quantity}{item.unit ? ` ${item.unit}` : ''}</td>
                  <td className="pinv-items__td pinv-items__td--num" data-label="Unit price"><Money amount={item.unit_price} /></td>
                  <td className="pinv-items__td pinv-items__td--num" data-label="Amount">
                    {/* The two figures are wrapped so they stay ONE thing
                        however the cell is laid out. Below the card
                        breakpoint the cell is a flex row, and two loose
                        children are two flex ITEMS: measured 2026-09-13 at
                        320px, "$1,125.00" and "$1,012.50" rendered side by
                        side on the same line, reading as two amounts rather
                        than a price and its discount. No figure changed. */}
                    <span className="pinv-items__amount">
                      {disc > 0 && <span className="pinv-items__wasprice"><Money amount={gross} /></span>}
                      <Money amount={net} />
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {/* ── Totals ───────────────────────────────────────────────── */}
        <section className="pinv-totals">
          <div className="pinv-totals__rows">
            <div className="pinv-totals__row">
              <span>Subtotal</span>
              <Money amount={invoice.subtotal} />
            </div>
            {/* `invoice.discount_amount` is the RAW input the office typed:
                dollars when discount_type is 'fixed', a PERCENT when it is
                'percent' (routes/invoices.js — "Persist raw discount input +
                type"). Rendering it straight through <Money> printed
                "Discount −$10.00" under a $2,000 subtotal on an invoice whose
                total was $1,800, to the person being asked to pay it. Resolved
                the same way the per-line row below already does, and the same
                way the server resolved it to reach that total. */}
            {(() => {
              const invDisc = resolveDiscount(
                invoice.subtotal, invoice.discount_amount, invoice.discount_type,
              );
              const isPct = invoice.discount_type === 'percent'
                && parseFloat(invoice.discount_amount) > 0;
              return invDisc > 0 ? (
                <div className="pinv-totals__row pinv-totals__row--positive">
                  <span>
                    Discount
                    {isPct && (
                      <span className="pinv-totals__row-desc fcp-tabular"> ({parseFloat(invoice.discount_amount)}%)</span>
                    )}
                    {invoice.discount_description && (
                      <span className="pinv-totals__row-desc"> ({invoice.discount_description})</span>
                    )}
                  </span>
                  <span>−<Money amount={invDisc} /></span>
                </div>
              ) : null;
            })()}
            {/* Per-line discounts (XOR with the invoice-level one): sum + show. */}
            {(() => {
              // computeLineItems, the SERVER's own function — the one that
              // built the stored total. Not a third hand-rolled sum.
              //
              // This row was left on the raw gross when the Discount COLUMN
              // above it was settled to cents, so one figure had two rules
              // with the column printed directly over the row. MEASURED across
              // 115,472 (quantity, price, percent) combinations: 5,766
              // disagreed — 0.5 x $0.01 at 50% gives $0.01 in the column and
              // $0.00 in the row, which does not even render. Going through
              // the server's function is what stops the two being edited apart
              // again.
              const { lineDiscountTotal: lineDisc } = computeLineItems(items || []);
              return lineDisc > 0 ? (
                <div className="pinv-totals__row pinv-totals__row--positive">
                  <span>Line-item discounts</span>
                  <span>−<Money amount={lineDisc} /></span>
                </div>
              ) : null;
            })()}
            {parseFloat(invoice.tax_rate) > 0 && (
              <div className="pinv-totals__row">
                <span>Tax <span className="pinv-totals__row-desc fcp-tabular">({invoice.tax_rate}%)</span></span>
                <Money amount={invoice.tax_amount} />
              </div>
            )}
            <div className="pinv-totals__row pinv-totals__row--grand">
              <span>Total</span>
              <Money amount={invoice.total} />
            </div>
            {parseFloat(invoice.amount_paid) > 0 && (
              <>
                <div className="pinv-totals__row pinv-totals__row--positive">
                  <span>Paid to date</span>
                  <span>−<Money amount={invoice.amount_paid} /></span>
                </div>
                <div className="pinv-totals__row pinv-totals__row--grand">
                  <span>Balance due</span>
                  <Money amount={Math.max(0, (parseFloat(invoice.total) || 0) - (parseFloat(invoice.amount_paid) || 0))} />
                </div>
              </>
            )}
            {parseFloat(invoice.deposit_amount) > 0 && (
              <div className="pinv-totals__row pinv-totals__row-desc">
                <span>Deposit requested</span>
                <Money amount={invoice.deposit_amount} />
              </div>
            )}
          </div>
          {invoice.payment_terms && (
            <div className="pinv-totals__terms">Payment terms: {invoice.payment_terms}</div>
          )}
        </section>

        {/* ── Notes ─────────────────────────────────────────────────── */}
        {invoice.notes && (
          <section className="pinv-notes">
            <div className="pinv-notes__label">Notes</div>
            <p className="pinv-notes__body">{invoice.notes}</p>
          </section>
        )}

        {/* ── Photos ────────────────────────────────────────────────── */}
        {photos && photos.length > 0 && (
          <section className="pinv-photos">
            <h3 className="pinv-photos__heading">Job photos</h3>
            <div className="pinv-photos__grid">
              {photos.map((photo, idx) => (
                <figure key={idx} className="pinv-photo">
                  <img
                    src={photo.file_path
                      ? (photo.file_path.startsWith('/uploads')
                          ? photo.file_path
                          : `/uploads/${photo.file_path}`)
                      : ''}
                    alt={photo.caption || 'Job photo'}
                    className="pinv-photo__img"
                    onError={e => {
                      e.target.src = '';
                      e.target.alt = 'Image not found';
                    }}
                  />
                  {(photo.caption || photo.media_caption) && (
                    <figcaption className="pinv-photo__caption">
                      {photo.caption || photo.media_caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* ── Just-paid banner (Stripe return) ──────────────────────── */}
        {/* "A confirmation has been sent to your email" used to be said here;
            nothing in the app sends one, so it is not claimed. Back from Stripe
            before the payment has reached us, the page says it is on its way
            rather than showing the bill as if nothing happened. */}
        {justPaid && (
          <section className="pinv-justpaid">
            <h3 className="pinv-justpaid__heading">Payment received</h3>
            <p className="pinv-justpaid__body">
              Thank you — we have received your payment.
            </p>
          </section>
        )}
        {returnedFromPay && !isPaid && (
          <section className="pinv-justpaid" data-payment-processing>
            <h3 className="pinv-justpaid__heading">Thank you</h3>
            <p className="pinv-justpaid__body" role="status">
              Your payment is being processed. It can take a minute to show here — there is no need to pay again.
            </p>
          </section>
        )}

        {/* ── Pay button ────────────────────────────────────────────── */}
        {!isPaid && !returnedFromPay && invoice.status !== 'cancelled' && (
          <section className="pinv-paywrap">
            <PayButton
              token={token}
              total={Math.max(0, (parseFloat(invoice.total) || 0) - (parseFloat(invoice.amount_paid) || 0))}
              company={company}
            />
          </section>
        )}

        {/* ── Footer ────────────────────────────────────────────────── */}
        <footer className="pinv-foot">
          <div className="pinv-foot__thanks">Thank you for your business.</div>
          <div className="pinv-foot__company">
            {company?.name}
            {company?.phone && <span> · {company.phone}</span>}
            {company?.email && <span> · {company.email}</span>}
          </div>
        </footer>
      </article>

      <div className="pinv-actions no-print">
        {/* The same PDF the email attaches (server/utils/invoicePdf.js). */}
        {/* An anchor wearing the Print button's class: inline-block so its
            padding makes a box like the button's instead of overlapping its
            neighbours (an <a> is inline), not underlined, and on the button's
            own `line-height: normal` rather than the page's, so the two pills
            are the same height (43px against 38px before). The {' '} after it
            is the space between two inline controls, which JSX would
            otherwise strip so the two touch. Set here, not in
            PublicInvoice.css, which is the protected client palette
            (CLAUDE.md). */}
        <a href={`/api/invoices/public/${token}/pdf`} className="pinv-print" style={{ display: 'inline-block', textDecoration: 'none', lineHeight: 'normal' }} data-action="download-pdf" download>
          Download PDF
        </a>
        {' '}
        <button onClick={() => window.print()} className="pinv-print">
          Print
        </button>
      </div>
    </div>
  );
}

export default PublicInvoice;
